import React, { useState, Fragment, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { BuyerInfoUser, InvitationStatus } from '@/types';
import { resendResidentialUnitBuyerInvitation } from '@/services/residential-units';
import { Trans, Plural } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Button } from '@/components/Buttons';
import theme from './BuyerInviteResendDialog.module.scss';
import { faUserCheck } from '@fortawesome/pro-light-svg-icons';
import { UserIcon } from '@/components/Icons';
import { DateDisplay } from '@/components/localization';
import { SlideToggle } from '@ygtechnologies/rubus-ui';
interface BuyerProfileInfoModalProps {
    show: boolean;
    onClose(): void;
    onAccept(sentInvites: BuyerInfoUser[]): void;
    buyers: BuyerInfoUser[];
}

const BuyerInviteResendDialog: React.FC<BuyerProfileInfoModalProps> = ({ show, onClose, onAccept, buyers }) => {
    const [saving, setSaving] = useState(false);
    const [sendEmail, setSendEmail] = useState(buyers.map((b) => false));
    const [sendSms, setSendSms] = useState(buyers.map((b) => false));

    useEffect(() => {
        setSendEmail(buyers.map((b) => b.inviteStatus === InvitationStatus.Pending));
    }, [buyers]);

    const reset = () => {
        setSendEmail(buyers.map((b) => false));
        setSendSms(buyers.map((b) => false));
    };

    const handleCloseClick = () => {
        onClose();
    };

    const handleAccept = async () => {
        setSaving(true);

        try {
            let nrOfInvites = 0;
            const sentInvites: BuyerInfoUser[] = [];
            for (let i = 0; i < buyers.length; i++) {
                if (sendEmail[i] || sendSms[i]) {
                    nrOfInvites++;
                    await resendResidentialUnitBuyerInvitation(buyers[i].inviteId, {
                        sendEmail: sendEmail[i],
                        sendSms: sendSms[i],
                    });
                    sentInvites.push(buyers[i]);
                }
            }

            const singular = buyers[0].inviteStatus === InvitationStatus.Pending ? t`Invitation has been sent` : t`Invitation has been resent`
            const plural = buyers[0].inviteStatus === InvitationStatus.Pending ? t`Invitations have been sent` : t`Invitations have been resent`

            Toast.success(
                <Plural
                    one={singular}
                    other={plural}
                    value={nrOfInvites}
                />
            );
            onAccept(sentInvites);
            onClose();
        } catch (error) {
            Toast.error(<Trans>Failed to send invitation. Please try again.</Trans>);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={show} size="lg" toggle={handleCloseClick} onOpen={reset} onClosed={reset}>
            <ModalHeader>
                {
                    buyers.length > 1 ? (
                        <Trans>Resend invitations</Trans>
                    ) : (
                        buyers[0].inviteStatus === InvitationStatus.Pending ? (
                            <Trans>Send invitation</Trans>
                        ) : (
                            <Trans>Resend invitation</Trans>
                        )
                    )
                }
            </ModalHeader>
            <ModalBody>
                {buyers.map((buyer, index) => (
                    <Fragment key={buyer.inviteId}>
                        <div className={theme.userColumn}>
                            <UserIcon
                                emptyIcon={faUserCheck}
                                sizeInPx={30}
                                userId={buyer.userId}
                                className={theme.userIcon}
                            />
                            <div className={theme.userTextSection}>
                                {buyer.displayName}
                                <small>
                                    {
                                        buyer.inviteStatus === InvitationStatus.Pending ? (
                                            <>
                                                <Trans>Added without invitation:</Trans>{" "}
                                                <DateDisplay value={buyer.invitedDate!} />

                                            </>
                                        ) : (
                                            <>
                                                <Trans>Invitation sent: </Trans>
                                                <DateDisplay value={buyer.invitedDate!} />
                                            </>
                                        )
                                    }
                                </small>
                            </div>
                        </div>
                        <div className={theme.modalBody}>
                            <div className={theme.column}>
                                <SlideToggle
                                    disabled={buyer.inviteStatus === InvitationStatus.Pending}
                                    onChange={(e) =>
                                        setSendEmail(Object.assign([], sendEmail, { [index]: e.target.checked }))
                                    }
                                    checked={sendEmail[index] || buyer.inviteStatus === InvitationStatus.Pending}>
                                    {
                                        buyer.inviteStatus === InvitationStatus.Pending ? (
                                            <Trans>Send invitation to {buyer.email}</Trans>
                                        ) : (
                                            <Trans>Resend to {buyer.email}</Trans>
                                        )
                                    }
                                </SlideToggle>
                            </div>
                            {buyer.phoneNumber ? (
                                <div className={theme.column}>
                                    <SlideToggle
                                        disabled={!buyer.phoneNumber}
                                        onChange={(e) =>
                                            setSendSms(Object.assign([], sendSms, { [index]: e.target.checked }))
                                        }
                                        checked={sendSms[index]}>
                                        {
                                            buyer.inviteStatus === InvitationStatus.Pending ? (
                                                <Trans>Send invitation to {buyer.phoneNumber}</Trans>
                                            ) : (
                                                <Trans>Resend to {buyer.phoneNumber}</Trans>
                                            )
                                        }
                                    </SlideToggle>
                                </div>
                            ) : (
                                <div />
                            )}
                        </div>
                        {index !== buyers.length - 1 && <div className={theme.divider} />}
                    </Fragment>
                ))}
            </ModalBody>
            <ModalFooter>
                <Button color="tertiary" onClick={handleCloseClick} disabled={saving}>
                    <Trans>Cancel</Trans>
                </Button>
                <Button color="primary" onClick={handleAccept} loading={saving} disabled={!sendEmail.some(e => e)}>
                    <Trans>Send</Trans>
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default BuyerInviteResendDialog;
